import {
  formatBytecodeSize,
  formatBytecodeVersion,
  shortenHash,
} from "@gearbox-protocol/sdk/permissionless";
import {
  Checkbox,
  ConnectRequired,
  CopyButton,
  ExternalButton,
  formatTimestamp,
  Label,
  PageLayout,
  SearchBar,
  SortingTableHead,
  TabButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@gearbox-protocol/ui-kit";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hex, hexToString } from "viem";
import { UploadBytecodeDialog } from "@/components/bytecode/dialog-upload";
import { BytecodeShort } from "@/core/bytecode";

interface BytecodeRepositoryListViewProps {
  bytecodes: BytecodeShort[];
}

function parseContractType(contractType: string) {
  return hexToString(contractType as `0x${string}`, { size: 32 });
}

export function BytecodeRepositoryListView({
  bytecodes,
}: BytecodeRepositoryListViewProps) {
  const navigate = useNavigate();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  const [sortBy, setSortBy] = useState<{
    key: "contractType" | "size" | "uploadedAt";
    direction: "asc" | "desc";
  }>({
    key: "contractType",
    direction: "asc",
  });

  const filteredBytecodes = useMemo(() => {
    const searchFiltered = bytecodes.filter(
      bytecode =>
        parseContractType(bytecode.contractType)
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        bytecode.contractType.toLowerCase() === searchQuery.toLowerCase() ||
        bytecode.bytecodeHash.toString() === searchQuery.toLowerCase(),
    );

    if (showAll) {
      return searchFiltered;
    }

    const maxByContractType = new Map<string, (typeof searchFiltered)[0]>();

    for (const bytecode of searchFiltered) {
      const contractType = bytecode.contractType;
      const existing = maxByContractType.get(contractType);
      if (!existing || bytecode.version > existing.version) {
        maxByContractType.set(contractType, bytecode);
      }
    }

    return Array.from(maxByContractType.values()).filter(
      ({ auditorNames }) => auditorNames.length > 0,
    );
  }, [bytecodes, searchQuery, showAll]);

  const sortedBytecodes = useMemo(() => {
    const sorted = [...filteredBytecodes].sort((a, b) => {
      // Items with no auditors always go to the end
      if (a.auditorNames.length === 0 && b.auditorNames.length > 0) {
        return 1;
      }
      if (b.auditorNames.length === 0 && a.auditorNames.length > 0) {
        return -1;
      }

      // Primary sort by sortBy.key
      let comparison = 0;
      if (sortBy.key === "contractType") {
        const aType = parseContractType(a.contractType);
        const bType = parseContractType(b.contractType);
        comparison = aType.localeCompare(bType);
      } else if (sortBy.key === "size") {
        comparison = a.size - b.size;
      } else if (sortBy.key === "uploadedAt") {
        comparison = a.uploadedAt - b.uploadedAt;
      }

      if (comparison !== 0) {
        return sortBy.direction === "asc" ? comparison : -comparison;
      }

      // Secondary sort by parseContractType(contractType) (if not already sorted by it)
      if (sortBy.key !== "contractType") {
        const aType = parseContractType(a.contractType);
        const bType = parseContractType(b.contractType);
        comparison = aType.localeCompare(bType);
        if (comparison !== 0) {
          return comparison;
        }
      }

      // Tertiary sort by version
      comparison = a.version - b.version;
      if (comparison !== 0) {
        return -comparison;
      }

      // Quaternary sort by auditorNames length
      comparison = a.auditorNames.length - b.auditorNames.length;
      if (comparison !== 0) {
        return -comparison;
      }

      // Fifth sort by uploadedAt (if not already sorted by it)
      if (sortBy.key !== "uploadedAt") {
        return a.uploadedAt - b.uploadedAt;
      }

      return 0;
    });

    return sorted;
  }, [filteredBytecodes, sortBy]);

  const handleRowClick = (hash: Hex) => {
    navigate(`/hash/${hash}`);
  };

  return (
    <PageLayout title="Bytecode Repository">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <SearchBar
            onChange={setSearchQuery}
            placeholder="Search conrtact type or bytecode hash"
            className="w-[328px]"
          />

          <div className="flex items-center space-x-2 h-8 border-l border-input pl-4">
            <Checkbox
              id="showAll"
              checked={showAll}
              onCheckedChange={checked => setShowAll(checked as boolean)}
            />
            <Label htmlFor="showAll" className="text-sm mb-0 cursor-pointer">
              Show all versions
            </Label>
          </div>
        </div>

        <ConnectRequired hideConnectButton>
          <TabButton onClick={() => setIsUploadDialogOpen(true)}>
            Upload Bytecode
          </TabButton>
        </ConnectRequired>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <SortingTableHead
              title="name"
              isSorted={sortBy.key === "contractType"}
              direction={sortBy.direction}
              setSort={d => setSortBy({ key: "contractType", direction: d })}
              className="w-[500px]"
            >
              Name
            </SortingTableHead>
            <TableHead className="w-[120px]">Version</TableHead>
            <SortingTableHead
              title="size"
              isSorted={sortBy.key === "size"}
              direction={sortBy.direction}
              setSort={d => setSortBy({ key: "size", direction: d })}
              className="w-[120px]"
            >
              Size
            </SortingTableHead>
            <TableHead>Audited by</TableHead>
            <SortingTableHead
              title="upload time"
              className="text-right [&>div]:ml-auto"
              placement="right"
              isSorted={sortBy.key === "uploadedAt"}
              direction={sortBy.direction}
              setSort={d => setSortBy({ key: "uploadedAt", direction: d })}
            >
              Uploaded at
            </SortingTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedBytecodes.map(bytecode => (
            <TooltipProvider key={bytecode.bytecodeHash}>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TableRow
                    className="transition-colors duration-200 cursor-pointer"
                    onClick={() => handleRowClick(bytecode.bytecodeHash)}
                  >
                    <TableCell>
                      {parseContractType(bytecode.contractType)}
                    </TableCell>
                    <TableCell>
                      {formatBytecodeVersion(bytecode.version)}
                    </TableCell>
                    <TableCell>{formatBytecodeSize(bytecode.size)}</TableCell>
                    <TableCell
                      className={
                        bytecode.auditorNames.length > 0
                          ? "text-success"
                          : "text-destructive"
                      }
                    >
                      {bytecode.auditorNames.length > 0
                        ? bytecode.auditorNames
                            .map(auditor => auditor)
                            .join(", ")
                        : "Not audited"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right">
                      {formatTimestamp(bytecode.uploadedAt)}
                    </TableCell>
                  </TableRow>
                </TooltipTrigger>

                <TooltipContent
                  side="top"
                  align="start"
                  alignOffset={24}
                  className="p-4"
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 justify-between mb-4 border-b border-muted-foreground pb-1">
                      <span className="font-semibold">
                        {parseContractType(bytecode.contractType)}
                      </span>
                      <span className="font-semibold">
                        {formatBytecodeVersion(bytecode.version)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 justify-between">
                      <span className="font-semibold text-md">
                        Bytecode Hash:
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">
                          {shortenHash(bytecode.bytecodeHash)}
                        </span>
                        <CopyButton text={bytecode.bytecodeHash} name="Hash" />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 justify-between">
                      <span className="font-semibold text-md">Author:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">
                          {shortenHash(bytecode.author)}
                        </span>
                        <CopyButton text={bytecode.author} />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 justify-between">
                      <span className="font-semibold text-md">Source:</span>
                      <div className="flex items-center gap-2">
                        <ExternalButton url={bytecode.source} size="sm" />
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </TableBody>
      </Table>

      {isUploadDialogOpen && (
        <UploadBytecodeDialog onClose={() => setIsUploadDialogOpen(false)} />
      )}
    </PageLayout>
  );
}
